import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import BackTextButton from "../../components/BackTextButton";
import Panel from "../../components/core/Components/Panel";
import useFetch from "../../Hooks/useFetch";
import IconButton from "../../components/IconButton";
import {faCircleXmark, faPlus, faTrashCan} from "@fortawesome/free-solid-svg-icons";
import {toast} from "react-toastify";
import ActionModal from "../../components/Modals/ActionModals/ActionModal";
import {deleteWorkOrder, putWorkOrderAssignments} from "../../API/companyApi";
import {Accordion, Badge, Col, Container, FormControl, FormLabel, Row, Table} from "react-bootstrap";
import {PiPolygonLight} from "react-icons/pi";
import NoElementsAlert from "../../components/NoElementsAlert";
import {formatDateTimeString} from "../../utils/utils";
import {formatDateForDateInput, isCurrentTimeBetween} from "../../utils/DateTimeUtills";
import LoadingSpinner from "../../components/Loading/LoadingSpinner";
import ConditionalRender from "../../components/ConditionalRender";
import {useTranslation} from "react-i18next";

const WorkOrderPage = () => {

    const {workOrderId} = useParams()
    const navigate = useNavigate()
    const {t} = useTranslation()

    const { data: workOrder, isLoading, error, refetch } = useFetch('v1/orders/id/' + workOrderId);
    const { data: geofencesAvailableForAssignment, refetch: refetchAvailableGeofences } = useFetch('v1/orders/' + workOrderId + '/geofences');

    const [showDeleteModal, setShowDeleteModal] = useState(false)

    const [intervalForCreation, setIntervalForCreation] = useState(() => {
        const from = new Date();
        from.setMinutes(0);
        from.setSeconds(0);

        // Create a new date that is 1 year after 'from'
        const to = new Date(from);
        to.setFullYear(to.getFullYear() + 1);

        return {
            from: from,
            to: to,
            geofenceId: null,
            showModal: false
        };
    })
    const [intervalForDeletion, setIntervalForDeletion] = useState({geofenceId: null, index: null})

    function updateFrom(event)
    {
        const date = new Date(event)
        setIntervalForCreation(prev => ({ ...prev, from: date }));
    }

    function updateTo(event)
    {
        const date = new Date(event)
        setIntervalForCreation(prev => ({ ...prev, to: date }));
    }


    const onWorkOrderDeletionConfirmed = async () => {
        await deleteWorkOrder(workOrderId)
    }

    const onWorkOrderIntervalCreation = async () => {
        //Insert the geofence if it's not in the list
        const workOrderHasGeofenceAlreadyAssigned = workOrder.assignedGeofences.some(a => a.geofenceId === intervalForCreation.geofenceId);
        if(!workOrderHasGeofenceAlreadyAssigned)
        {
            workOrder.assignedGeofences.push({geofenceId: intervalForCreation.geofenceId, intervals: [{start: intervalForCreation.from, end: intervalForCreation.to}]})
        }
        else {
            workOrder.assignedGeofences.map(a => {
                if(a.geofenceId === intervalForCreation.geofenceId)
                    a.intervals.push({start: intervalForCreation.from, end: intervalForCreation.to})
                return a
            })
        }

        await putWorkOrderAssignments(workOrderId, workOrder.assignedGeofences)
        refetch()
        if(!workOrderHasGeofenceAlreadyAssigned)
        {
            refetchAvailableGeofences()
        }
    }


    const deleteIntervalAtIndex = async (geofenceId, index) => {
        setIntervalForDeletion({geofenceId, index})

        const workOrders = structuredClone(workOrder.assignedGeofences)


        workOrders.map(a => {
            if(a.geofenceId === geofenceId)
                a.intervals = a.intervals.slice(0, index).concat(a.intervals.slice(index + 1))
            return a
        })


        await putWorkOrderAssignments(workOrderId, workOrders)
        if(workOrders.find(a => a.geofenceId === geofenceId).intervals.length < 1)
            refetchAvailableGeofences()

        setIntervalForDeletion({geofenceId: null, index: null})
        refetch()
        toast.success("Deleted")
    }

    const getBadgeForInterval = (interval) => {
        const isActive = isCurrentTimeBetween(interval.start, interval.end)
        return isActive ? <Badge pill bg={'success'}>{t('pages.workOrders.workOrderDetails.status.active')}</Badge> : <Badge pill bg={'danger'}>{t('pages.workOrders.workOrderDetails.status.inactive')}</Badge>
    }

    const getBadgeForAssignment = (assignment) => {
        const isActive = assignment.intervals.some(i => isCurrentTimeBetween(i.start, i.end))
        return isActive ? <Badge pill bg={'success'} className={'ms-2'}>{t('pages.workOrders.workOrderDetails.status.active')}</Badge> : <Badge pill bg={'danger'} className={'ms-2'}>{t('pages.workOrders.workOrderDetails.status.inactive')}</Badge>
    }

    if(error)
        return null;

    if(isLoading)
        return <Panel><LoadingSpinner small/></Panel>

    return (
        <div className={'px-4 py-1'}>
            <BackTextButton text='Back' className='mb-3'/>
            <Panel>
                <LoadingSpinner small show={isLoading}/>
                <div className={'p-2'}>
                    <div className={'d-flex justify-content-between'}>
                        <div className={'d-flex align-items-center'}>
                            <h5 className='mb-0'>{workOrder.name}</h5>
                            <Badge className={'ms-2'} bg={'secondary'}>{workOrder.key}</Badge>
                        </div>
                        <IconButton icon={faTrashCan} color={'white'} bg={'red'} onClick={() => setShowDeleteModal(true)}/>
                    </div>
                    <hr/>

                    <Container fluid>
                        <Row>
                            <Col>
                                <div className={'d-flex align-items-center justify-content-between mb-2'}>
                                    <span className='caption'>Assigned geofences</span>
                                </div>
                                <Accordion alwaysOpen>
                                    {workOrder.assignedGeofences?.map(geofenceAssignment => (
                                        <Accordion.Item key={geofenceAssignment.geofenceId}
                                                        eventKey={geofenceAssignment.geofenceId}>
                                            <Accordion.Header>
                                                <div className={'d-flex align-items-center'}>
                                                    <PiPolygonLight/>
                                                    <span className={'ms-1'}>{geofenceAssignment.geofenceName}</span>
                                                </div>
                                                {getBadgeForAssignment(geofenceAssignment)}
                                            </Accordion.Header>
                                            <Accordion.Body>
                                                <Table className={'border-0 border-secondary-subtle mb-2'}>
                                                    <thead>
                                                    <tr>
                                                        <th>{t('global.time.start')}</th>
                                                        <th>{t('global.time.end')}</th>
                                                        <th>{t('pages.workOrders.workOrderDetails.status.status')}</th>
                                                        <th width={120}>{t('global.operations.manage')}</th>
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    {
                                                        geofenceAssignment.intervals.map((interval, index) => (
                                                            <tr key={index}>
                                                                <td>{formatDateTimeString(interval.start)}</td>
                                                                <td>{formatDateTimeString(interval.end)}</td>
                                                                <td>{getBadgeForInterval(interval)}</td>
                                                                <td>
                                                                    {
                                                                        intervalForDeletion.geofenceId === geofenceAssignment.geofenceId && intervalForDeletion.index === index ?
                                                                            <LoadingSpinner small variant={'danger'} className={'w-25'}/> :
                                                                            <IconButton icon={faTrashCan}
                                                                                        color={'white'} bg={'red'}
                                                                                        onClick={() => deleteIntervalAtIndex(geofenceAssignment.geofenceId, index)}/>
                                                                    }

                                                                </td>
                                                            </tr>
                                                        ))
                                                    }
                                                    </tbody>
                                                </Table>

                                                <div className={'center mt-1'}>
                                                    <IconButton icon={faPlus} color="white" bg="#198754" onClick={() => {
                                                        setIntervalForCreation(prev => ({ ...prev, geofenceId: geofenceAssignment.geofenceId, showModal: true }))
                                                    }}/>
                                                    <span className={'ms-2 text-secondary'}>{t('pages.workOrders.workOrderDetails.addInterval')}</span>
                                                </div>

                                                {/*<hr className={'mb-1 mt-2'}/>*/}
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    ))}
                                </Accordion>
                                <NoElementsAlert show={workOrder.assignedGeofences?.length < 1}
                                                 text="No assigned geofences" icon={faCircleXmark}
                                                 variant={'secondary'}/>

                                <ConditionalRender condition={geofencesAvailableForAssignment?.length > 0}>
                                    <div className={'center mt-3'}>
                                        <IconButton icon={faPlus} color="white" bg="#198754" onClick={() => {
                                            setIntervalForCreation(prev => ({ ...prev, geofenceId:  geofencesAvailableForAssignment[0].geofenceId, showModal: true }))
                                        }}/>
                                    </div>
                                </ConditionalRender>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </Panel>

            <ActionModal
                func={onWorkOrderDeletionConfirmed}
                active={showDeleteModal}
                positiveButton={{}}
                hideOnConfirm
                onCancel={() => {setShowDeleteModal(false)}}
                onConfirm={()=> {toast.success("Deleted"); navigate(-1)}}
                title={t('dialogs.confirmDeleteDialog.title')}>

                {<span>{t('dialogs.confirmDeleteDialog.youAreAboutTo')} <strong style={{color: 'red'}}>{t('dialogs.confirmDeleteDialog.delete')}</strong> {t('pages.workOrders.workOrder')} <strong>{workOrder.name}</strong>. {t('dialogs.confirmDeleteDialog.areYouSure')}</span>}
            </ActionModal>

            <ActionModal
                func={onWorkOrderIntervalCreation}
                active={intervalForCreation.showModal}
                positiveButton={{
                    text: t("global.operations.create"),
                    variant: 'success',
                    disabled: !intervalForCreation.geofenceId
                }}
                hideOnConfirm
                onCancel={() => {setIntervalForCreation(prev => ({ ...prev, geofenceId: null, showModal: false }))}}
                onConfirm={()=> {toast.success("Created")}}
                title={t("pages.workOrders.workOrderDetails.createIntervalDialog.title")}>

                <ConditionalRender condition={!intervalForCreation.geofenceId || geofencesAvailableForAssignment.some(x => x.geofenceId === intervalForCreation.geofenceId)}>
                    <FormLabel>{t("pages.workOrders.workOrderDetails.createIntervalDialog.selectGeofence")}</FormLabel>
                    <FormControl as='select' className={'mb-2'} onChange={(e) => {
                        setIntervalForCreation(prev => ({ ...prev, geofenceId: parseInt(e.target.value) }))
                    }}>
                        {geofencesAvailableForAssignment?.map(g => <option key={g.geofenceId} value={g.geofenceId}>{g.geofenceName}</option>)}
                    </FormControl>
                </ConditionalRender>

                <FormLabel>{t("global.time.from")}</FormLabel>
                <FormControl required type='datetime-local' value={formatDateForDateInput(intervalForCreation.from)} onChange={(e) => updateFrom(e.target.value)} />
                <FormLabel className={'mt-2'}>{t("global.time.to")}</FormLabel>
                <FormControl required type='datetime-local' min={formatDateForDateInput(intervalForCreation.from)} value={formatDateForDateInput(intervalForCreation.to)} onChange={(e) => updateTo(e.target.value)} />
            </ActionModal>
        </div>
    );
};

export default WorkOrderPage;